import React, { useState, useEffect } from 'react';
import { useUrlState } from '../hooks/useUrlParams';
import { drivingSchoolAPI } from '../services/api';
import './Brands.css';

function DrivingSchools() {
  const [schools, setSchools] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ currentCity: '', cityIndex: 0, totalCities: 0 });
  const [syncResults, setSyncResults] = useState([]);
  const [syncComplete, setSyncComplete] = useState(false);
  const [showSyncOptions, setShowSyncOptions] = useState(false);
  const [lastSyncProgress, setLastSyncProgress] = useState(null);

  // Pagination state
  const [page, setPage] = useUrlState('page', 1);
  const [limit, setLimit] = useUrlState('limit', 10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filter state
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('');

  // Create/Edit modal state
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    address: '',
    city: '',
    pincode: '',
    telephone: '',
    latitude: '',
    longitude: ''
  });
  const [formLoading, setFormLoading] = useState(false);

  // Fetch unique cities from driving schools (for filter dropdown)
  const fetchCities = async () => {
    try {
      const response = await drivingSchoolAPI.getCities();
      setCities(response.data || []);
    } catch (err) {
      console.error('Failed to fetch cities:', err);
    }
  };

  const fetchSchools = async (currentPage, currentLimit, currentSearch, currentCity) => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: currentLimit
      };
      if (currentSearch) params.search = currentSearch;
      if (currentCity) params.city = currentCity;

      const response = await drivingSchoolAPI.getAll(params);

      if (Array.isArray(response.data)) {
        setSchools(response.data);
        setTotal(response.data.length);
        setTotalPages(1);
      } else {
        setSchools(response.data.data || []);
        setTotal(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.totalPages || 0);
      }
      setError('');
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to fetch driving schools');
      setSchools([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch sync progress on load
  const fetchSyncProgress = async () => {
    try {
      const response = await drivingSchoolAPI.getSyncProgress();
      setLastSyncProgress(response.data);
    } catch (err) {
      console.error('Failed to fetch sync progress:', err);
    }
  };

  useEffect(() => {
    fetchCities();
    fetchSyncProgress();
  }, []);

  useEffect(() => {
    fetchSchools(page, limit, search, cityFilter);
  }, [page, limit, search, cityFilter]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this driving school?')) {
      try {
        await drivingSchoolAPI.delete(id);
        fetchSchools(page, limit, search, cityFilter);
      } catch (err) {
        setError('Failed to delete driving school');
      }
    }
  };

  // Open create modal
  const handleCreate = () => {
    setEditingSchool(null);
    setFormData({
      title: '',
      address: '',
      city: cityFilter || '',
      pincode: '',
      telephone: '',
      latitude: '',
      longitude: ''
    });
    setShowFormModal(true);
  };

  // Open edit modal
  const handleEdit = (school) => {
    setEditingSchool(school);
    setFormData({
      title: school.title || '',
      address: school.address || '',
      city: school.city || '',
      pincode: school.pincode || '',
      telephone: school.telephone || '',
      latitude: school.latitude || '',
      longitude: school.longitude || ''
    });
    setShowFormModal(true);
  };

  // Handle form input change
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Submit form (create or update)
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setError('');

    try {
      if (editingSchool) {
        await drivingSchoolAPI.update(editingSchool.id, formData);
      } else {
        await drivingSchoolAPI.create(formData);
      }
      setShowFormModal(false);
      fetchSchools(page, limit, search, cityFilter);
      fetchCities();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save driving school');
    } finally {
      setFormLoading(false);
    }
  };

  // Close form modal
  const closeFormModal = () => {
    setShowFormModal(false);
    setEditingSchool(null);
    setFormData({ title: '', address: '', city: '', pincode: '', telephone: '', latitude: '', longitude: '' });
  };

  // Sync with mode: 'smart' (default), 'full', 'resume'
  const handleSyncAll = async (mode = 'smart') => {
    setShowSyncOptions(false);
    setSyncing(true);
    setShowSyncModal(true);
    setSyncComplete(false);
    setSyncResults([]);

    let progressMessage = 'Processing all cities...';
    let subMessage = 'This may take several minutes';

    if (mode === 'smart') {
      progressMessage = 'Smart Sync: Checking for changes...';
      subMessage = 'Skipping cities where DB matches API count';
    } else if (mode === 'resume') {
      progressMessage = 'Resuming from last position...';
      subMessage = lastSyncProgress?.lastCityName ? `Continuing from ${lastSyncProgress.lastCityName}` : 'Starting sync';
    } else if (mode === 'full') {
      progressMessage = 'Full Sync: Processing all cities...';
      subMessage = 'Re-syncing all data from beginning';
    }

    setSyncProgress({
      currentCity: progressMessage,
      subMessage: subMessage,
      cityIndex: 0,
      totalCities: 1
    });

    try {
      // Call the server-side sync-all endpoint with mode
      const response = await drivingSchoolAPI.syncAll(mode);
      const data = response.data;

      if (data.success || data.totalSynced !== undefined) {
        setSyncResults(data.results || []);
        setSyncProgress({
          currentCity: 'Complete',
          subMessage: `Synced ${data.totalSynced || 0} driving schools${data.skippedCities ? `, skipped ${data.skippedCities} cities` : ''}`,
          cityIndex: data.totalCities || 0,
          totalCities: data.totalCities || 0
        });
      } else {
        setError(data.error || 'Sync failed');
      }
    } catch (err) {
      console.error('Sync error:', err);
      setError(err.response?.data?.error || 'Failed to sync driving schools');
    }

    setSyncComplete(true);
    setSyncing(false);
    fetchSchools(page, limit, search, cityFilter);
    fetchCities();
    fetchSyncProgress();
  };

  // Reset sync progress
  const handleResetSyncProgress = async () => {
    try {
      await drivingSchoolAPI.resetSyncProgress();
      setLastSyncProgress(null);
      setShowSyncOptions(false);
    } catch (err) {
      setError('Failed to reset sync progress');
    }
  };

  const closeSyncModal = () => {
    setShowSyncModal(false);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleCityFilterChange = (e) => {
    setCityFilter(e.target.value);
    setPage(1);
  };

  const getTotalSynced = () => {
    return syncResults.reduce((sum, r) => sum + (r.count || 0), 0);
  };

  const getProgressPercent = () => {
    if (syncComplete) {
      return 100;
    }
    if (syncing && syncProgress.totalCities <= 1) {
      return -1; // Signal for indeterminate
    }
    return syncProgress.totalCities > 0 ? Math.round((syncProgress.cityIndex / syncProgress.totalCities) * 100) : 0;
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${page === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <div className="pagination-info">
          Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} entries
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(1)}
            disabled={page === 1}
          >
            First
          </button>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
          >
            Prev
          </button>
          {pages}
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
          >
            Next
          </button>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(totalPages)}
            disabled={page === totalPages}
          >
            Last
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="brands-page">
      {/* Sync Progress Modal */}
      {showSyncModal && (
        <div className="modal-overlay">
          <div className="sync-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{syncComplete ? 'Sync Complete' : 'Syncing Driving Schools...'}</h2>
              {syncComplete && <button className="modal-close" onClick={closeSyncModal}>&times;</button>}
            </div>
            <div className="modal-body sync-modal-body">
              {syncing && (
                <div className="sync-progress">
                  <div className="spinner"></div>
                  <p className="sync-status">
                    <strong>{syncProgress.currentCity}</strong>
                  </p>
                  <p className="sync-counts">
                    {syncProgress.subMessage}
                  </p>
                  <div className="progress-bar">
                    <div
                      className={`progress-fill ${getProgressPercent() === -1 ? 'indeterminate' : ''}`}
                      style={{ width: getProgressPercent() === -1 ? '30%' : `${getProgressPercent()}%` }}
                    ></div>
                  </div>
                  <p className="progress-percent">{getProgressPercent() === -1 ? 'Please wait...' : `${getProgressPercent()}%`}</p>
                </div>
              )}

              {syncComplete && syncResults.length > 0 && (
                <div className="sync-results">
                  <h3>Total: {getTotalSynced()} driving schools synced</h3>
                  <div className="results-grid">
                    {syncResults.filter(r => r.count > 0).map((result, index) => (
                      <div key={index} className="result-item success">
                        <span className="city-name">{result.city}</span>
                        <span className="count">{result.count} schools</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {syncComplete && (
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={closeSyncModal}>
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showFormModal && (
        <div className="modal-overlay" onClick={closeFormModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSchool ? 'Edit Driving School' : 'Add Driving School'}</h2>
              <button className="modal-close" onClick={closeFormModal}>&times;</button>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    required
                    placeholder="Driving school name"
                  />
                </div>
                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleFormChange}
                    required
                    placeholder="City name"
                  />
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleFormChange}
                    rows="2"
                    placeholder="Full address"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="text"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleFormChange}
                      placeholder="Phone number"
                    />
                  </div>
                  <div className="form-group">
                    <label>Pincode</label>
                    <input
                      type="text"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleFormChange}
                      placeholder="Pincode"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Latitude</label>
                    <input
                      type="text"
                      name="latitude"
                      value={formData.latitude}
                      onChange={handleFormChange}
                      placeholder="e.g., 28.6139"
                    />
                  </div>
                  <div className="form-group">
                    <label>Longitude</label>
                    <input
                      type="text"
                      name="longitude"
                      value={formData.longitude}
                      onChange={handleFormChange}
                      placeholder="e.g., 77.2090"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeFormModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={formLoading}>
                  {formLoading ? 'Saving...' : (editingSchool ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="page-header">
        <h1>Driving Schools</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={handleCreate}>
            + Add School
          </button>
          <div className="sync-dropdown">
            <button
              className="btn btn-sync"
              onClick={() => setShowSyncOptions(!showSyncOptions)}
              disabled={syncing}
            >
              {syncing ? 'Syncing...' : 'Sync Data'} ▼
            </button>
            {showSyncOptions && (
              <div className="sync-options-menu">
                <button onClick={() => handleSyncAll('smart')} className="sync-option">
                  <strong>Smart Sync</strong>
                  <span>Skip cities where count matches</span>
                </button>
                {lastSyncProgress && lastSyncProgress.status === 'in_progress' && (
                  <button onClick={() => handleSyncAll('resume')} className="sync-option resume">
                    <strong>Resume Sync</strong>
                    <span>Continue from {lastSyncProgress.lastCityName || 'last position'}</span>
                  </button>
                )}
                <button onClick={() => handleSyncAll('full')} className="sync-option">
                  <strong>Full Sync</strong>
                  <span>Re-sync all cities from beginning</span>
                </button>
                {lastSyncProgress && lastSyncProgress.status !== 'not_started' && (
                  <button onClick={handleResetSyncProgress} className="sync-option danger">
                    <strong>Reset Progress</strong>
                    <span>Clear sync tracking data</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="table-controls">
          <div className="table-filters">
            <input
              type="text"
              placeholder="Search by name, address, city..."
              value={search}
              onChange={handleSearchChange}
              className="search-input"
            />
            <select value={cityFilter} onChange={handleCityFilterChange} className="filter-select">
              <option value="">All Cities</option>
              {cities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>
          <div className="table-options">
            <label>
              Show
              <select value={limit} onChange={handleLimitChange}>
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </select>
              entries
            </label>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading...</div>
        ) : schools.length === 0 ? (
          <div className="empty-state">
            <p>No driving schools found. {search || cityFilter ? 'Try adjusting your filters.' : 'Click "Sync Data" to fetch driving schools!'}</p>
          </div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>City</th>
                  <th>Address</th>
                  <th>Phone</th>
                  <th>Pincode</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {schools.map((school) => (
                  <tr key={school.id}>
                    <td>{school.id}</td>
                    <td>{school.title}</td>
                    <td>{school.city}</td>
                    <td className="address-cell">{school.address || '-'}</td>
                    <td>{school.telephone || '-'}</td>
                    <td>{school.pincode || '-'}</td>
                    <td>
                      <div className="action-buttons">
                        {school.latitude && school.longitude && (
                          <a
                            href={`https://www.google.com/maps?q=${school.latitude},${school.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-view"
                          >
                            Map
                          </a>
                        )}
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEdit(school)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(school.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && renderPagination()}
          </>
        )}
      </div>

      <style jsx>{`
        .sync-dropdown {
          position: relative;
          display: inline-block;
        }
        .sync-options-menu {
          position: absolute;
          top: 100%;
          right: 0;
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          min-width: 250px;
          z-index: 1000;
          margin-top: 5px;
        }
        .sync-option {
          display: block;
          width: 100%;
          padding: 12px 16px;
          border: none;
          background: none;
          text-align: left;
          cursor: pointer;
          border-bottom: 1px solid #eee;
        }
        .sync-option:last-child {
          border-bottom: none;
        }
        .sync-option:hover {
          background-color: #f5f5f5;
        }
        .sync-option strong {
          display: block;
          color: #333;
          margin-bottom: 2px;
        }
        .sync-option span {
          display: block;
          font-size: 12px;
          color: #666;
        }
        .sync-option.resume {
          background-color: #e3f2fd;
        }
        .sync-option.resume:hover {
          background-color: #bbdefb;
        }
        .sync-option.danger strong {
          color: #dc3545;
        }
        .sync-option.danger:hover {
          background-color: #ffebee;
        }
        .sync-progress {
          text-align: center;
          padding: 20px;
        }
        .sync-status {
          margin: 15px 0 5px;
          font-size: 16px;
        }
        .sync-counts {
          color: #666;
          font-size: 14px;
          margin-bottom: 15px;
        }
        .progress-percent {
          margin-top: 8px;
          font-weight: bold;
          color: #007bff;
        }
        .sync-results {
          margin-top: 10px;
        }
        .sync-results h3 {
          margin-bottom: 15px;
          color: #28a745;
          text-align: center;
        }
        .results-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: 10px;
          max-height: 300px;
          overflow-y: auto;
        }
        .result-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 15px;
          border-radius: 4px;
          font-size: 14px;
        }
        .result-item.success {
          background-color: #d4edda;
          color: #155724;
        }
        .result-item .city-name {
          font-weight: 500;
        }
        .result-item .count {
          font-size: 12px;
          opacity: 0.8;
        }
        .progress-bar {
          width: 100%;
          height: 10px;
          background-color: #e9ecef;
          border-radius: 5px;
          overflow: hidden;
        }
        .progress-fill {
          height: 100%;
          background-color: #007bff;
          transition: width 0.3s ease;
        }
        .progress-fill.indeterminate {
          animation: indeterminate 1.5s infinite ease-in-out;
        }
        @keyframes indeterminate {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(200%); }
          100% { transform: translateX(-100%); }
        }
        .address-cell {
          max-width: 250px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .header-actions {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          backdrop-filter: blur(4px);
        }
        .modal {
          background: white;
          border-radius: 12px;
          max-width: 520px;
          width: 95%;
          max-height: 90vh;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }
        .modal-header {
          padding: 20px 24px;
          border-bottom: 1px solid #eee;
          display: flex;
          justify-content: flex-start;
          align-items: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          position: relative;
        }
        .modal-header h2 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          text-align: left;
        }
        .modal-close {
          background: rgba(255,255,255,0.2);
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
        }
        .modal-close:hover {
          background: rgba(255,255,255,0.3);
        }
        .modal-body {
          padding: 24px;
          max-height: calc(90vh - 160px);
          overflow-y: auto;
        }
        .modal-footer {
          padding: 16px 24px;
          border-top: 1px solid #eee;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          background: #fafafa;
        }
        .form-group {
          margin-bottom: 16px;
          text-align: left;
        }
        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-weight: 600;
          color: #333;
          font-size: 14px;
          text-align: left;
        }
        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 14px;
        }
        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .btn-secondary {
          background: #f0f0f0;
          color: #333;
          border: 1px solid #ddd;
        }
        .btn-secondary:hover {
          background: #e0e0e0;
        }
      `}</style>
    </div>
  );
}

export default DrivingSchools;
